"use client";

import { c as _c } from "react/compiler-runtime";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import Link, { type LinkProps } from "next/link";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * Touch screens only: icon buttons get an invisible tap area 4px wider on each side (the gap
 * between adjacent buttons) and 6px taller (rows are at least 40px apart), so neighbours never
 * overlap. Mouse layouts and the visible size are unchanged.
 */
const iconTouchArea = "relative pointer-coarse:after:absolute pointer-coarse:after:-inset-x-1 pointer-coarse:after:-inset-y-1.5";
const buttonVariants = cva("group/button focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-xs/relaxed font-medium whitespace-nowrap transition outline-none select-none focus-visible:ring-2 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", {
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground hover:bg-primary/80",
      /** Marketing / public landing primary — brand lime, not product sage. */
      marketing: "bg-brand-primary text-brand-primary-foreground hover:bg-brand-primary/90 shadow-sm",
      outline: "border-border hover:bg-input/50 hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:bg-input/30",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
      ghost: "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
      destructive: "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
      /** Quiet destructive action (row delete icons); tints only on hover. */
      "ghost-destructive": "text-destructive hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:hover:bg-destructive/20 dark:focus-visible:ring-destructive/40",
      link: "text-primary underline-offset-4 hover:underline"
    },
    size: {
      default: "h-7 gap-1 px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
      xs: "h-5 gap-1 rounded-sm px-2 text-[0.625rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-2.5",
      sm: "h-8 gap-1 px-2.5 text-xs/relaxed has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3.5",
      lg: "h-10 gap-1.5 px-3 text-sm has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-4",
      /** Marketing / hero CTAs on public pages. */
      hero: "h-12 gap-2 rounded-lg px-8 text-base has-data-[icon=inline-end]:pr-6 has-data-[icon=inline-start]:pl-6 [&_svg:not([class*='size-'])]:size-5",
      icon: `size-7 [&_svg:not([class*='size-'])]:size-3.5 ${iconTouchArea}`,
      "icon-xs": `size-5 rounded-sm [&_svg:not([class*='size-'])]:size-2.5 ${iconTouchArea}`,
      "icon-sm": `size-8 [&_svg:not([class*='size-'])]:size-4 ${iconTouchArea}`,
      "icon-lg": `size-10 [&_svg:not([class*='size-'])]:size-4 ${iconTouchArea}`
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function Button(t0) {
  const $ = _c(16);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      type: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "default" : t2;
  const type = t3 === undefined ? "button" : t3;
  let t4;
  if ($[6] !== className || $[7] !== size || $[8] !== variant) {
    t4 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[6] = className;
    $[7] = size;
    $[8] = variant;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props || $[11] !== size || $[12] !== t4 || $[13] !== type || $[14] !== variant) {
    t5 = <ButtonPrimitive data-slot="button" data-variant={variant} data-size={size} className={t4} type={type} {...props} />;
    $[10] = props;
    $[11] = size;
    $[12] = t4;
    $[13] = type;
    $[14] = variant;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}

// Anchor props are added explicitly: generated typed-route types (`.next/types`) put them on
// `LinkProps`, but a clean checkout (CI) only has the base `LinkProps` without them.
type ButtonLinkProps<RouteType> = LinkProps<RouteType> & Omit<ComponentProps<"a">, keyof LinkProps<RouteType>> & VariantProps<typeof buttonVariants>;

/**
 * Navigation styled as a button. Renders a real `Link` (link role), unlike
 * `Button render={<Link />}`, which Base UI exposes as a button.
 */
function ButtonLink(t0) {
  const $ = _c(14);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "default" : t2;
  let t3;
  if ($[5] !== className || $[6] !== size || $[7] !== variant) {
    t3 = cn(buttonVariants({
      variant,
      size,
      className
    }));
    $[5] = className;
    $[6] = size;
    $[7] = variant;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== size || $[11] !== t3 || $[12] !== variant) {
    t4 = <Link data-slot="button" data-variant={variant} data-size={size} className={t3} {...props} />;
    $[9] = props;
    $[10] = size;
    $[11] = t3;
    $[12] = variant;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export { Button, ButtonLink, buttonVariants };